// ---------------------------------------------------------------------------
// CaseSettingsForm — React Hook Form + Zod for case-level settings
// ---------------------------------------------------------------------------

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  CaseSurveySettings,
  DeliveryChannel,
  EffectiveCaseConfig,
  UpdateCaseSettingsRequest,
} from '@/types/config';

const DELIVERY_CHANNELS: { value: DeliveryChannel; label: string }[] = [
  { value: 'voice', label: 'Voice' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'web', label: 'Web' },
  { value: 'mobile', label: 'Mobile' },
];

const caseSettingsSchema = z.object({
  maxCallsPerDay: z.coerce.number().int().min(0).max(99),
  ignoreAlertTriggered: z.boolean(),
  ignoreNoncompliance: z.boolean(),
  ignoreWeekendAlertTriggered: z.boolean(),
  deliveryTypePreference: z.enum(['voice', 'sms', 'email', 'web', 'mobile']),
});

type CaseSettingsValues = z.infer<typeof caseSettingsSchema>;

interface CaseSettingsFormProps {
  effective: EffectiveCaseConfig;
  settings: CaseSurveySettings | null;
  onSubmit: (values: UpdateCaseSettingsRequest) => void;
  onCancel: () => void;
  isPending: boolean;
}

/** Returns true when the case has its own override for a field */
function hasOverride(settings: CaseSurveySettings | null, field: keyof CaseSurveySettings): boolean {
  if (!settings) return false;
  return settings[field] != null;
}

function SourceBadge({ isCustom }: { isCustom: boolean }) {
  return isCustom ? (
    <Badge variant="default" className="ml-2 text-[10px]">Custom</Badge>
  ) : (
    <Badge variant="secondary" className="ml-2 text-[10px]">Inherited</Badge>
  );
}

export function CaseSettingsForm({
  effective,
  settings,
  onSubmit,
  onCancel,
  isPending,
}: CaseSettingsFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CaseSettingsValues>({
    resolver: zodResolver(caseSettingsSchema),
    defaultValues: {
      maxCallsPerDay: effective.maxCallsPerDay,
      ignoreAlertTriggered: effective.ignoreAlertTriggered,
      ignoreNoncompliance: effective.ignoreNoncompliance,
      ignoreWeekendAlertTriggered: effective.ignoreWeekendAlertTriggered,
      deliveryTypePreference: (effective.deliveryTypePreference as DeliveryChannel) || 'voice',
    },
  });

  const submit = (values: CaseSettingsValues) => {
    onSubmit(values);
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-4">
      <div className="max-w-md space-y-4">
        {/* Max Calls per Day */}
        <div>
          <div className="flex items-center mb-1">
            <Label htmlFor="maxCallsPerDay">Max Calls per Day</Label>
            <SourceBadge isCustom={hasOverride(settings, 'max_calls_per_day')} />
          </div>
          <Input
            id="maxCallsPerDay"
            type="number"
            min={0}
            max={99}
            {...register('maxCallsPerDay')}
          />
          {errors.maxCallsPerDay && (
            <p className="mt-1 text-xs text-red-600">{errors.maxCallsPerDay.message}</p>
          )}
        </div>

        {/* Ignore Alert Triggered */}
        <div className="flex items-center gap-3">
          <input
            id="ignoreAlertTriggered"
            type="checkbox"
            {...register('ignoreAlertTriggered')}
            className="h-4 w-4 rounded border-silver-300 text-purple-600 focus:ring-purple-500"
          />
          <Label htmlFor="ignoreAlertTriggered" className="mb-0">Ignore Alert Triggered</Label>
          <SourceBadge isCustom={hasOverride(settings, 'ignore_alert_triggered')} />
        </div>

        {/* Ignore Noncompliance */}
        <div className="flex items-center gap-3">
          <input
            id="ignoreNoncompliance"
            type="checkbox"
            {...register('ignoreNoncompliance')}
            className="h-4 w-4 rounded border-silver-300 text-purple-600 focus:ring-purple-500"
          />
          <Label htmlFor="ignoreNoncompliance" className="mb-0">Ignore Noncompliance</Label>
          <SourceBadge isCustom={hasOverride(settings, 'ignore_noncompliance')} />
        </div>

        {/* Ignore Weekend Alert Triggered */}
        <div className="flex items-center gap-3">
          <input
            id="ignoreWeekendAlertTriggered"
            type="checkbox"
            {...register('ignoreWeekendAlertTriggered')}
            className="h-4 w-4 rounded border-silver-300 text-purple-600 focus:ring-purple-500"
          />
          <Label htmlFor="ignoreWeekendAlertTriggered" className="mb-0">Ignore Weekend Alert Triggered</Label>
          <SourceBadge isCustom={hasOverride(settings, 'ignore_weekend_alert_triggered')} />
        </div>

        {/* Delivery Preference */}
        <div>
          <div className="flex items-center mb-1">
            <Label htmlFor="deliveryTypePreference">Delivery Preference</Label>
            <SourceBadge isCustom={hasOverride(settings, 'delivery_type_preference')} />
          </div>
          <Select
            value={watch('deliveryTypePreference')}
            onValueChange={(v) => setValue('deliveryTypePreference', v as DeliveryChannel)}
          >
            <SelectTrigger id="deliveryTypePreference">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DELIVERY_CHANNELS.map((ch) => (
                <SelectItem key={ch.value} value={ch.value}>{ch.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </form>
  );
}
